/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef, useId} from 'react'
import {Icon, useStyleConfig, omitThemingProps} from '@chakra-ui/react'

// Our own SVG imports. These will be extracted to a single sprite sheet by the
// svg-sprite-loader webpack plugin at build time and injected in the <body> tag
// during SSR.
// NOTE: Another solution would be to use `require-context.macro` package to accomplish
// importing icon svg's.
import '../../assets/svg/_360.svg'
import '../../assets/svg/alert.svg'
import '../../assets/svg/account.svg'
import '../../assets/svg/apple.svg'
import '../../assets/svg/ar.svg'
import '../../assets/svg/arrow.svg'
import '../../assets/svg/arrow-pinned.svg'
import '../../assets/svg/arrow-left-round.svg'
import '../../assets/svg/arrow-right-round.svg'
import '../../assets/svg/arrow-right2.svg'
import '../../assets/svg/availability.svg'
import '../../assets/svg/basket.svg'
import '../../assets/svg/bin-bg-white.svg'
import '../../assets/svg/box-2-outline.svg'
import '../../assets/svg/cart.svg'
import '../../assets/svg/check.svg'
import '../../assets/svg/checkbox-disabled.svg'
import '../../assets/svg/check-circle.svg'
import '../../assets/svg/chevron-up.svg'
import '../../assets/svg/chevron-down.svg'
import '../../assets/svg/chevron-right.svg'
import '../../assets/svg/chevron-left.svg'
import '../../assets/svg/chevron-right.svg'
import '../../assets/svg/chevron-up.svg'
import '../../assets/svg/configurator-3d.svg'
import '../../assets/svg/colors-indicator.svg'
import '../../assets/svg/dashboard.svg'
import '../../assets/svg/delete.svg'
import '../../assets/svg/delivery-fast-outline.svg'
import '../../assets/svg/discount.svg'
import '../../assets/svg/edit.svg'
import '../../assets/svg/eye.svg'
import '../../assets/svg/figma-logo.svg'
import '../../assets/svg/filter.svg'
import '../../assets/svg/file.svg'
import '../../assets/svg/github-logo.svg'
import '../../assets/svg/google.svg'
import '../../assets/svg/gps-location.svg'
import '../../assets/svg/hamburger.svg'
import '../../assets/svg/hotspot.svg'
import '../../assets/svg/info.svg'
import '../../assets/svg/info-i.svg'
import '../../assets/svg/social-facebook.svg'
import '../../assets/svg/social-instagram.svg'
import '../../assets/svg/social-pinterest.svg'
import '../../assets/svg/social-twitter.svg'
import '../../assets/svg/social-youtube.svg'
import '../../assets/svg/social-weibo.svg'
import '../../assets/svg/social-wechat.svg'
import '../../assets/svg/social-tiktok.svg'
import '../../assets/svg/social-xiaohongshu.svg'
import '../../assets/svg/like.svg'
import '../../assets/svg/lock.svg'
import '../../assets/svg/lock-fill.svg'
import '../../assets/svg/logout.svg'
import '../../assets/svg/marker-pin-01.svg'
import '../../assets/svg/minus.svg'
import '../../assets/svg/message-question-mark.svg'
import '../../assets/svg/navigation-arrow-left.svg'
import '../../assets/svg/navigation-arrow-right.svg'
import '../../assets/svg/payment.svg'
import '../../assets/svg/play.svg'
import '../../assets/svg/plug.svg'
import '../../assets/svg/plus.svg'
import '../../assets/svg/plus-round.svg'
import '../../assets/svg/point.svg'
import '../../assets/svg/question-mark.svg'
import '../../assets/svg/receipt.svg'
import '../../assets/svg/round-plus.svg'
import '../../assets/svg/search.svg'
import '../../assets/svg/share.svg'
import '../../assets/svg/signout.svg'
import '../../assets/svg/slide.svg'
import '../../assets/svg/status-off.svg'
import '../../assets/svg/status-on.svg'
import '../../assets/svg/store.svg'
import '../../assets/svg/storefinder.svg'
import '../../assets/svg/tooltip.svg'
import '../../assets/svg/unavailable-cart.svg'
import '../../assets/svg/user.svg'
import '../../assets/svg/verified.svg'
import '../../assets/svg/visibility.svg'
import '../../assets/svg/visibility-off.svg'
import '../../assets/svg/warning.svg'
import '../../assets/svg/close.svg'
import '../../assets/svg/success.svg'
import '../../assets/svg/review-star-filled.svg'
import '../../assets/svg/review-star-half-filled.svg'
import '../../assets/svg/review-star-empty.svg'
import '../../assets/svg/zoom.svg'
import '../../assets/svg/zoom-out.svg'
import '../../assets/svg/history.svg'
import '../../assets/svg/save-extra.svg'
import '../../assets/svg/decagon-checked.svg'
import '../../assets/svg/car.svg'
import '../../assets/svg/hand-dollar.svg'
import '../../assets/svg/gift.svg'
import '../../assets/svg/gift-box.svg'
import '../../assets/svg/bin.svg'
import '../../assets/svg/hearts.svg'
import '../../assets/svg/heart-message.svg'
import '../../assets/svg/free-shipping-flag.svg'
import '../../assets/svg/free-shipping-circle.svg'
import '../../assets/svg/thin-account.svg'
import '../../assets/svg/thin-basket.svg'
import '../../assets/svg/thin-heart.svg'
import '../../assets/svg/thin-payment.svg'
import '../../assets/svg/thin-return-order.svg'
import '../../assets/svg/thin-storefinder.svg'
import '../../assets/svg/thin-warranty.svg'

import './flags'
import './promotions'

// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
import AmexSymbol from '../../assets/svg/cc-amex.svg'
import ArrowLeftSymbol from '../../assets/svg/arrow-left.svg'
import ArrowLeftRound2Symbol from '../../assets/svg/arrow-left-round2.svg'
import ArrowRightSymbol from '../../assets/svg/arrow-right.svg'
import ArrowRightRound2Symbol from '../../assets/svg/arrow-right-round2.svg'
import ArrowLeftRightSymbol from '../../assets/svg/arrow-left-right.svg'
import BackArrowLeft from '../../assets/svg/back-arrow-left.svg'
import BrandLogoSmallSymbol from '../../assets/svg/brand-logo-small.svg'
import BrandLogoSymbol from '../../assets/svg/brand-logo.svg'
import BreadcrumbSeparatorSymbol from '../../assets/svg/breadcrumb-separator.svg'
import CheckVerified02Symbol from '../../assets/svg/check-verified-02.svg'
import CheckVerified03Symbol from '../../assets/svg/check-verified-03.svg'
import ColorSelectionSymbol from '../../assets/svg/color-selection.svg'
import ColorsIndicatorSymbol from '../../assets/svg/colors-indicator.svg'
import CrossVerified02Symbol from '../../assets/svg/cross-verified-02.svg'
import CVVSymbol from '../../assets/svg/cc-cvv.svg'
import DHLSymbol from '../../assets/svg/dhl.svg'
import DHLExpressSymbol from '../../assets/svg/dhl-express.svg'
import DiscoverSymbol from '../../assets/svg/cc-discover.svg'
import DropdownSymbol from '../../assets/svg/dropdown.svg'
import KlarnaSymbol from '../../assets/svg/klarna.svg'
import LocationSymbol from '../../assets/svg/location.svg'
import MastercardSymbol from '../../assets/svg/cc-mastercard.svg'
import ReturnOrderSymbol from '../../assets/svg/return-order.svg'
import SendSymbol from '../../assets/svg/send.svg'
import SofortSymbol from '../../assets/svg/sofort.svg'
import StokkeWordBrandLogoSymbol from '../../assets/svg/stokke-word-brand.svg'
import VisaSymbol from '../../assets/svg/cc-visa.svg'
import LineSymbol from '../../assets/svg/line.svg'
import ThumbDownSymbol from '../../assets/svg/thumb-down.svg'
import ThumbDownActiveSymbol from '../../assets/svg/thumb-down-active.svg'
import ThumbUpSymbol from '../../assets/svg/thumb-up.svg'
import ThumbUpActiveSymbol from '../../assets/svg/thumb-up-active.svg'
import WarrantySymbol from '../../assets/svg/warranty.svg'
import HeartIconSymbol from '../../assets/svg/heart.svg'
import HeartBGWhiteIconSymbol from '../../assets/svg/heart-bg-white.svg'
import HeartSolidIconSymbol from '../../assets/svg/heart-solid.svg'

import PropTypes from 'prop-types'

// TODO: We're hardcoding the `viewBox` for these imported SVGs temporarily as the
// SVG loader plugin is not properly providing us the symbol data on the client side.
AmexSymbol.viewBox = AmexSymbol.viewBox || '0 0 38 22'
ArrowLeftSymbol.viewBox = ArrowLeftSymbol.viewBox || '0 0 16 17'
ArrowLeftRound2Symbol.viewBox = ArrowLeftRound2Symbol.viewBox || '0 0 24 25'
ArrowRightSymbol.viewBox = ArrowRightSymbol.viewBox || '0 0 16 17'
ArrowRightRound2Symbol.viewBox = ArrowRightRound2Symbol.viewBox || '0 0 24 25'
ArrowLeftRightSymbol.viewBox = ArrowLeftRightSymbol.viewBox || '0 0 17 16'
BackArrowLeft.viewBox = BackArrowLeft.viewBox || '0 0 16 18'
BrandLogoSmallSymbol.viewBox = BrandLogoSmallSymbol.viewBox || '0 0 34 33'
BrandLogoSymbol.viewBox = BrandLogoSymbol.viewBox || '0 0 56 56'
BreadcrumbSeparatorSymbol.viewBox = BreadcrumbSeparatorSymbol.viewBox || '0 0 6 9'
CheckVerified02Symbol.viewBox = CheckVerified02Symbol.viewBox || '0 0 16 17'
CheckVerified03Symbol.viewBox = CheckVerified03Symbol.viewBox || '0 0 24 25'
CrossVerified02Symbol.viewBox = CrossVerified02Symbol.viewBox || '0 0 16 17'
CVVSymbol.viewBox = CVVSymbol.viewBox || '0 0 41 24'
ColorSelectionSymbol.viewBox = ColorSelectionSymbol.viewBox || '0 0 65 32'
ColorsIndicatorSymbol.viewBox = ColorsIndicatorSymbol.viewBox || '0 0 38 18'
DHLSymbol.viewBox = DHLSymbol.viewBox || '0 0 90 20'
DHLExpressSymbol.viewBox = DHLExpressSymbol.viewBox || '0 0 90 28'
DiscoverSymbol.viewBox = DiscoverSymbol.viewBox || '0 0 38 22'
KlarnaSymbol.viewBox = KlarnaSymbol.viewBox || '0 0 90 20'
DropdownSymbol.viewBox = DropdownSymbol.viewBox || '0 0 5 4'
LocationSymbol.viewBox = LocationSymbol.viewBox || '0 0 16 21'
MastercardSymbol.viewBox = MastercardSymbol.viewBox || '0 0 38 22'
ReturnOrderSymbol.viewBox = ReturnOrderSymbol.viewBox || '0 0 16 17'
SendSymbol.viewBox = SendSymbol.viewBox || '0 0 17 16'
SofortSymbol.viewBox = SofortSymbol.viewBox || '0 0 63 20'
StokkeWordBrandLogoSymbol.viewBox = '0 0 119 23'
VisaSymbol.viewBox = VisaSymbol.viewBox || '0 0 63 20'
LineSymbol.viewBox = LineSymbol.viewBox || '0 0 24 1'
ThumbDownActiveSymbol.viewBox = ThumbDownActiveSymbol.viewBox || '0 0 22 21'
ThumbDownSymbol.viewBox = ThumbDownSymbol.viewBox || '0 0 22 21'
ThumbUpActiveSymbol.viewBox = ThumbUpActiveSymbol.viewBox || '0 0 22 21'
ThumbUpSymbol.viewBox = ThumbUpSymbol.viewBox || '0 0 22 21'
WarrantySymbol.viewBox = WarrantySymbol.viewBox || '0 0 16 17'
HeartIconSymbol.viewBox = HeartIconSymbol.viewBox || '0 0 21 20'
HeartBGWhiteIconSymbol.viewBox = HeartBGWhiteIconSymbol.viewBox || '0 0 24 21'
HeartSolidIconSymbol.viewBox = HeartSolidIconSymbol.viewBox || '0 0 24 21'

/**
 * A helper for creating a Chakra-wrapped icon from our own SVG imports via sprite sheet.
 * @param {string} name - the filename of the imported svg (does not include extension)
 */
/* istanbul ignore next */
const icon = (name, passProps) => {
    const displayName = name
        .toLowerCase()
        .replace(/(?:^|[\s-/])\w/g, (match) => match.toUpperCase())
        .replace(/-/g, '')
    const component = forwardRef((props, ref) => {
        const titleId = useId()
        const mergedProps = {...passProps, ...props}
        const style = useStyleConfig('Icon', mergedProps)
        const {
            'aria-label': title,
            'aria-labelledby': labeledBy,
            ...ownProps
        } = omitThemingProps(mergedProps)

        return (
            <Icon
                ref={ref}
                {...style}
                {...ownProps}
                {...(title
                    ? {'aria-labelledby': titleId}
                    : labeledBy
                    ? {'aria-labelledby': labeledBy}
                    : {})}
            >
                {title && <title id={titleId}>{title}</title>}
                <use role="presentation" xlinkHref={`#${name}`} />
            </Icon>
        )
    })

    component.propTypes = {
        'aria-label': PropTypes.string,
        'aria-labelledby': PropTypes.string
    }

    if (process.env.NODE_ENV !== 'production') {
        component.displayName = `${displayName}Icon`
    }
    return component
}

// More explicit than just icon('AT')
export const FlagIcon = ({countryCode, ...props}) => {
    const Icon = icon(countryCode)
    // should be hardcoded for now https://dept-de.atlassian.net/browse/STOK-12283
    return countryCode === 'TW' ? '' : <Icon {...props} aria-hidden="true" />
}

FlagIcon.propTypes = {
    countryCode: PropTypes.string
}

// Export Chakra icon components that use our SVG sprite symbol internally
// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
export const _360Icon = icon('_360')
export const AccountIcon = icon('account')
export const AmexIcon = icon('cc-amex', {viewBox: AmexSymbol.viewBox})
export const AlertIcon = icon('alert')
export const AppleIcon = icon('apple')
export const ARIcon = icon('ar')
export const ArrowIcon = icon('arrow')
export const ArrowPinnedIcon = icon('arrow-pinned')
export const ArrowLeftIcon = icon('arrow-left', {viewBox: ArrowLeftSymbol.viewBox})
export const ArrowLeftRoundIcon = icon('arrow-left-round')
export const ArrowLeftRound2Icon = icon('arrow-left-round2', {
    viewBox: ArrowLeftRound2Symbol.viewBox
})
export const ArrowRightIcon = icon('arrow-right', {viewBox: ArrowRightSymbol.viewBox})
export const ArrowRight2Icon = icon('arrow-right2')
export const ArrowRightRoundIcon = icon('arrow-right-round')
export const ArrowRightRound2Icon = icon('arrow-right-round2', {
    viewBox: ArrowRightRound2Symbol.viewBox
})
export const ArrowLeftRight = icon('arrow-left-right', {viewBox: ArrowLeftRightSymbol.viewBox})
export const AvailabilityIcon = icon('availability')
export const BackArrowLeftIcon = icon('back-arrow-left', {viewBox: BackArrowLeft.viewBox})
export const BrandLogo = icon('brand-logo', {role: 'img', viewBox: BrandLogoSymbol.viewBox})
export const BrandLogoSmall = icon('brand-logo-small', {viewBox: BrandLogoSmallSymbol.viewBox})
export const StokkeWordBrandLogo = icon('stokke-word-brand', {
    viewBox: StokkeWordBrandLogoSymbol.viewBox
})
export const BasketIcon = icon('basket')
export const Box2OutlineIcon = icon('box-2-outline')
export const BreadcrumbSeparatorIcon = icon('breadcrumb-separator', {
    viewBox: BreadcrumbSeparatorSymbol.viewBox
})
export const CartIcon = icon('cart')
export const CheckIcon = icon('check')
export const CheckboxDisabledIcon = icon('checkbox-disabled')
export const CheckCircleIcon = icon('check-circle')
export const CheckVerified02Icon = icon('check-verified-02', {
    viewBox: CheckVerified02Symbol.viewBox
})
export const CheckVerified03Icon = icon('check-verified-03', {
    viewBox: CheckVerified03Symbol.viewBox
})
export const ChevronDownIcon = icon('chevron-down')
export const ChevronLeftIcon = icon('chevron-left')
export const ChevronRightIcon = icon('chevron-right')
export const ChevronUpIcon = icon('chevron-up')
export const ColorSelectionIcon = icon('color-selection', {viewBox: ColorSelectionSymbol.viewBox})
export const ColorsIndicator = icon('colors-indicator', {viewBox: ColorsIndicatorSymbol.viewBox})
export const Configurator3dIcon = icon('configurator-3d')
export const CrossVerified02Icon = icon('cross-verified-02', {
    viewBox: CrossVerified02Symbol.viewBox
})
export const CVVIcon = icon('cc-cvv', {viewBox: CVVSymbol.viewBox})
export const DashboardIcon = icon('dashboard')
export const DeleteIcon = icon('delete')
export const DeliveryFastOutlineIcon = icon('delivery-fast-outline')
export const DiscountIcon = icon('discount')
export const DiscoverIcon = icon('cc-discover', {viewBox: DiscoverSymbol.viewBox})
export const DhlIcon = icon('dhl', {viewBox: DHLSymbol.viewBox})
export const DhlExpressIcon = icon('dhl-express', {viewBox: DHLExpressSymbol.viewBox})
export const EditIcon = icon('edit')
export const EyeIcon = icon('eye')
export const FigmaLogo = icon('figma-logo')
export const FilterIcon = icon('filter')
export const FileIcon = icon('file')
export const GiftIcon = icon('gift')
export const GiftBoxIcon = icon('gift-box')
export const GithubLogo = icon('github-logo')
export const GoogleIcon = icon('google')
export const GpsLocationIcon = icon('gps-location')
export const HamburgerIcon = icon('hamburger')
export const HotspotIcon = icon('hotspot')
export const InfoIcon = icon('info')
export const InfoIconFilled = icon('info-i')
export const KlarnaIcon = icon('klarna', {viewBox: KlarnaSymbol.viewBox})
export const LikeIcon = icon('like')
export const LockIcon = icon('lock')
export const LockFillIcon = icon('lock-fill')
export const DropdownIcon = icon('dropdown', {
    viewBox: DropdownSymbol.viewBox
})
export const LocationIcon = icon('location')
export const LogoutIcon = icon('logout')
export const MarkerPin01Icon = icon('marker-pin-01')
export const MessageQuestionMarkIcon = icon('message-question-mark')
export const MinusIcon = icon('minus')
export const NavigationLeftIcon = icon('navigation-arrow-left')
export const NavigationRightIcon = icon('navigation-arrow-right')
export const PaymentIcon = icon('payment')
export const PlayIcon = icon('play')
export const PlugIcon = icon('plug')
export const PlusIcon = icon('plus')
export const PlusRoundIcon = icon('plus-round')
export const PointIcon = icon('point')
export const QuestionMarkIcon = icon('question-mark')
export const MastercardIcon = icon('cc-mastercard', {viewBox: MastercardSymbol.viewBox})
export const ReceiptIcon = icon('receipt')
export const ReturnOrderIcon = icon('return-order', {viewBox: ReturnOrderSymbol.viewBox})
export const RoundPlusIcon = icon('round-plus')
export const SearchIcon = icon('search')
export const SendIcon = icon('send', {viewBox: SendSymbol.viewBox})
export const ShareIcon = icon('share')
export const SocialFacebookIcon = icon('social-facebook')
export const SocialInstagramIcon = icon('social-instagram')
export const SocialPinterestIcon = icon('social-pinterest')
export const SocialTwitterIcon = icon('social-twitter')
export const SocialYoutubeIcon = icon('social-youtube')
export const SocialWeiboIcon = icon('social-weibo')
export const SocialWeChatIcon = icon('social-wechat')
export const SocialTikTokIcon = icon('social-tiktok')
export const SocialXiaohongshuIcon = icon('social-xiaohongshu')
export const SofortIcon = icon('sofort', {viewBox: SofortSymbol.viewBox})
export const SignoutIcon = icon('signout')
export const StatusOffIcon = icon('status-off')
export const StatusOnIcon = icon('status-on')
export const StoreIcon = icon('store')
export const StorefinderIcon = icon('storefinder')
export const ThinAccountIcon = icon('thin-account')
export const ThinBasketIcon = icon('thin-basket')
export const ThinHeartIcon = icon('thin-heart')
export const ThinPaymentIcon = icon('thin-payment')
export const ThinReturnOrderIcon = icon('thin-return-order')
export const ThinStorefinderIcon = icon('thin-storefinder')
export const ThinWarrantyIcon = icon('thin-warranty')
export const ThumbDownIcon = icon('thumb-down', {viewBox: ThumbDownSymbol.viewBox})
export const ThumbDownActiveIcon = icon('thumb-down-active', {
    viewBox: ThumbDownActiveSymbol.viewBox
})
export const ThumbUpIcon = icon('thumb-up', {viewBox: ThumbUpSymbol.viewBox})
export const ThumbUpActiveIcon = icon('thumb-up-active', {viewBox: ThumbUpActiveSymbol.viewBox})
export const TooltipIcon = icon('tooltip')
export const UnavailableCartIcon = icon('unavailable-cart')
export const UserIcon = icon('user')
export const VerifiedIcon = icon('verified')
export const VisaIcon = icon('cc-visa', {viewBox: VisaSymbol.viewBox})
export const VisibilityIcon = icon('visibility')
export const VisibilityOffIcon = icon('visibility-off')
export const WarningIcon = icon('warning')
export const HeartIcon = icon('heart', {viewBox: HeartIconSymbol.viewBox})
export const HeartBGWhiteIcon = icon('heart-bg-white', {viewBox: HeartBGWhiteIconSymbol.viewBox})
export const HeartSolidIcon = icon('heart-solid', {viewBox: HeartSolidIconSymbol.viewBox})
export const FreeShippingFlagIcon = icon('free-shipping-flag')
export const FreeShippingCircleIcon = icon('free-shipping-circle')
export const HeartsIcon = icon('hearts')
export const HeartMessageIcon = icon('heart-message')
export const CloseIcon = icon('close')
export const SuccessPaymentIcon = icon('success')
export const LineIcon = icon('line', {viewBox: LineSymbol.viewBox})
export const StarIcon = icon('review-star-filled')
export const StarHalfIcon = icon('review-star-half-filled')
export const StarEmptyIcon = icon('review-star-empty')
export const SlideIcon = icon('slide')
export const ZoomIcon = icon('zoom')
export const ZoomOutIcon = icon('zoom-out')
export const HistoryIcon = icon('history')
export const SaveExtraIcon = icon('save-extra')
export const DecagonCheckedIcon = icon('decagon-checked')
export const CarIcon = icon('car')
export const HandDollarIcon = icon('hand-dollar')
export const WarrantyIcon = icon('warranty', {viewBox: WarrantySymbol.viewBox})
export const BinIcon = icon('bin')
export const BinBGWhiteIcon = icon('bin-bg-white')
